import { useCallback } from 'react';
import axios from 'axios'
import { useQuery } from 'react-query';
import { useAppState } from '../redux';

type DataSource = {
  id: number
  label: string
  excludedFields?: string[]
}

type DataSourceMap = {
  [key: string]: DataSource
}

export const DATA_SOURCE: DataSourceMap = {
  ORG_EMPLOYEES: {
    id: 3,
    label: 'Employee Table'
  },
  JOBS: {
    id: 4,
    label: 'Job Table',
    excludedFields: ['payscale_job_info']
  }
};

async function getCategoryElements(maid: string, categoryCode: string, categoryTableFilter: DataSource[]) {
  return await axios
    .get(`/cms/api/accounts/${maid}/element-tree`, {
      params: {
        categoryCode,
        elementsTreeFlattened: false
      }
    })
    .then(result => {
      const elements: any[] = [];
      const dataSources = Object.values(DATA_SOURCE);
      const filteredDataSources =
        categoryTableFilter && categoryTableFilter.length > 0
          ? dataSources.filter(dataSource => categoryTableFilter.includes(dataSource))
          : dataSources;
      result?.data?.data?.tree?.forEach((category: any) => {
        const foundDataSource = filteredDataSources.find(dataSource => dataSource.label === category.text);
        if (categoryTableFilter.length === 0 || foundDataSource) {
          category.children?.forEach((section: any) => {
            section.children?.forEach((field: any) => {
              const dataSourceLength = field.nonaggregate_data_sources?.length;
              if (
                field.source_field &&
                !field.source_field.includes('->') &&
                !field.source_field.includes('udf_') &&
                !field.source_field.includes('date') && // not supporting dates on first pass
                dataSourceLength === 1 &&
                !field.formula_pk &&
                !shouldExcludeField(field, foundDataSource)
              ) {
                elements.push(field);
              }
            });
          });
        }
      });
      return elements;
    })
    .catch(error => {
      const errorMessage = `Error retrieving employee filter items`;
      console.error(`${errorMessage}: ${error}`);
      throw error;
    });
}

function shouldExcludeField(field: any, dataSource: DataSource | undefined) {
  if (!dataSource?.excludedFields) {
    return false;
  }
  if (dataSource.excludedFields.includes(field.source_field)) {
    return true;
  }
  if (dataSource.excludedFields.some((excludedField: string) => field.source_field.startsWith(`${excludedField} - `))) {
    return true;
  }
  return false;
}

export function useCategoryElements(categoryCode: string, categoryTableFilter: DataSource[]) {
  const appState = useAppState()
  const { masterAccountId } = appState.session
  const getUrl = `/cms/api/accounts/${masterAccountId}/element-tree`;
  const populateElements = useCallback(async () => await getCategoryElements(masterAccountId, categoryCode, categoryTableFilter), [
    getUrl,
    categoryCode,
    categoryTableFilter
  ]);
  return {
    key: getUrl,
    ...useQuery([getUrl, categoryCode, categoryTableFilter], populateElements, { staleTime: Infinity })
  };
}
